import { Select as BaseSelect } from '@base-ui/react/select'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import type { Ref } from 'react'
import { cn } from '../lib/cn'
import { useIsCoarsePointer } from '../lib/useMediaQuery'
import { LayerProvider, useLayer, Z } from './context'
import { controlClass, FieldFrame, useFieldIds } from './Field'
import { overlayClass } from './Popover'
import type { FieldProps, Option, Size } from './types'

export interface SelectProps<V extends string = string> extends FieldProps {
  value: V | null
  onChange: (value: V) => void
  options: readonly Option<V>[]
  /** „Bitte wählen“ */
  placeholder?: string
  size?: Size
  /** × setzt die Auswahl zurück */
  clearable?: boolean
  onClear?: () => void
  /** 'auto' (Standard): natives <select> bei grobem Zeiger */
  native?: boolean | 'auto'
  onBlur?: () => void
  ref?: Ref<HTMLElement>
}

function groupOptions<V extends string>(
  options: readonly Option<V>[],
): { group: string | undefined; options: Option<V>[] }[] {
  const groups: { group: string | undefined; options: Option<V>[] }[] = []
  for (const option of options) {
    const last = groups[groups.length - 1]
    if (last && last.group === option.group) last.options.push(option)
    else groups.push({ group: option.group, options: [option] })
  }
  return groups
}

/** Auswahl aus einer festen Liste (bis etwa 15 Einträge). Darüber oder bei Serverdaten: Combobox. */
export function Select<V extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Bitte wählen',
  size = 'md',
  clearable,
  onClear,
  native = 'auto',
  onBlur,
  ref,
  label,
  labelHidden,
  hint,
  error,
  required,
  disabled,
  readOnly,
  id,
  name,
  className,
  'data-testid': testId,
}: SelectProps<V>) {
  const ids = useFieldIds({ id, hint, error })
  const coarse = useIsCoarsePointer()
  const zIndex = useLayer(Z.popover)
  const useNative = native === true || (native === 'auto' && coarse)
  const framed = Boolean(label || hint || error)
  const groups = groupOptions(options)
  const canClear = clearable && value !== null && !disabled && !readOnly
  const frame = controlClass({ invalid: ids.invalid, disabled, readOnly, size })

  const control = useNative ? (
    <div className={cn('relative', !framed && className)}>
      <select
        ref={ref as Ref<HTMLSelectElement>}
        id={ids.id}
        name={name}
        value={value ?? ''}
        disabled={disabled || readOnly}
        required={required}
        aria-label={!label ? placeholder : undefined}
        aria-invalid={ids.invalid || undefined}
        aria-describedby={ids.describedBy}
        data-testid={testId}
        data-ui="field"
        onBlur={onBlur}
        onChange={(event) => {
          if (event.target.value === '') onClear?.()
          else onChange(event.target.value as V)
        }}
        className={cn(
          frame,
          'appearance-none pr-8 pl-(--control-px) outline-none',
          ids.invalid ? 'focus:field-focus-danger' : 'focus:field-focus',
          value === null && 'text-fg-subtle',
        )}
      >
        {(value === null || clearable) && <option value="">{placeholder}</option>}
        {groups.map((group, index) =>
          group.group ? (
            <optgroup key={`${group.group}-${index}`} label={group.group}>
              {group.options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ) : (
            group.options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))
          ),
        )}
      </select>
      <ChevronsUpDown
        className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-fg-subtle"
        strokeWidth={1.75}
        aria-hidden
      />
    </div>
  ) : (
    <div className={cn('relative', !framed && className)}>
      <BaseSelect.Root
        value={value}
        onValueChange={(next) => {
          if (next !== null && next !== undefined) onChange(next as V)
        }}
        items={options as Option<V>[]}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
        required={required}
      >
        <BaseSelect.Trigger
          ref={ref as Ref<HTMLButtonElement>}
          id={ids.id}
          aria-label={!label ? placeholder : undefined}
          aria-invalid={ids.invalid || undefined}
          aria-describedby={ids.describedBy}
          data-testid={testId}
          data-ui="field"
          onBlur={onBlur}
          className={cn(
            frame,
            'justify-between gap-2 pr-2 pl-(--control-px) text-left outline-none select-none',
            ids.invalid ? 'focus-visible:field-focus-danger' : 'focus-visible:field-focus data-popup-open:field-focus',
            canClear && 'pr-8',
          )}
        >
          <BaseSelect.Value placeholder={placeholder} className="truncate-line data-placeholder:text-fg-subtle" />
          <BaseSelect.Icon className="shrink-0 text-fg-subtle">
            <ChevronsUpDown className="size-4" strokeWidth={1.75} aria-hidden />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner alignItemWithTrigger={false} sideOffset={4} collisionPadding={8} style={{ zIndex }}>
            <BaseSelect.Popup
              className={cn(overlayClass, 'max-h-(--available-height) min-w-(--anchor-width) overflow-y-auto p-1')}
            >
              <LayerProvider z={zIndex}>
                <BaseSelect.List>
                  {groups.map((group, index) => {
                    const items = group.options.map((option) => {
                      const Icon = option.icon
                      return (
                        <BaseSelect.Item
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          className="flex min-h-8 cursor-default items-center gap-2 rounded-sm py-1 pr-2 pl-2 text-dense outline-none select-none touch:min-h-11 touch:text-touch data-disabled:text-fg-disabled data-highlighted:bg-overlay-hover"
                        >
                          {Icon && <Icon className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} aria-hidden />}
                          <span className="min-w-0 flex-1">
                            <BaseSelect.ItemText className="block truncate-line">{option.label}</BaseSelect.ItemText>
                            {option.description && (
                              <span className="block text-caption text-fg-subtle">{option.description}</span>
                            )}
                          </span>
                          <BaseSelect.ItemIndicator className="shrink-0">
                            <Check className="size-4" strokeWidth={2} aria-hidden />
                          </BaseSelect.ItemIndicator>
                        </BaseSelect.Item>
                      )
                    })
                    return group.group ? (
                      <BaseSelect.Group key={`${group.group}-${index}`}>
                        <BaseSelect.GroupLabel className="px-2 pt-2 pb-1 text-micro text-fg-subtle uppercase">
                          {group.group}
                        </BaseSelect.GroupLabel>
                        {items}
                      </BaseSelect.Group>
                    ) : (
                      items
                    )
                  })}
                </BaseSelect.List>
              </LayerProvider>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      {canClear && (
        <button
          type="button"
          aria-label="Auswahl zurücksetzen"
          onClick={() => onClear?.()}
          className="absolute top-1/2 right-7 flex size-5 -translate-y-1/2 items-center justify-center rounded-xs text-fg-subtle hover:bg-hover hover:text-fg touch:size-8"
        >
          <X className="size-3.5" strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  )

  if (!framed) return control
  return (
    <FieldFrame
      ids={ids}
      label={label}
      labelHidden={labelHidden}
      hint={hint}
      error={error}
      required={required}
      className={className}
    >
      {control}
    </FieldFrame>
  )
}
